// ============================================================================
// playerRender.js — draws & procedurally animates Benjen (a small Group rig, not
// instanced) from the logic-side player state, and feeds Masamune's endpoints to
// the sword trail. Interpolates position by the fixed-step alpha.
// ============================================================================

import * as THREE from 'three';
import { buildBenjen } from './meshes.js';
import { lerp, clamp01, angleDamp, TAU } from '../core/math.js';
import { PLAYER_SPEED, DODGE_TIME } from '../core/constants.js';

export class PlayerRender {
  constructor(scene, trail) {
    const rig = buildBenjen();
    this.group = rig.group;
    this.parts = rig.parts;
    this.group.rotation.order = 'YXZ';
    scene.add(this.group);
    this.trail = trail;
    this.phase = 0;
    this.renderHeading = Math.PI;
    this._top = new THREE.Vector3();
    this._bot = new THREE.Vector3();
    this._baseBladeEmiss = this.parts.blade.material.emissiveIntensity;
  }

  update(world, alpha, dt) {
    const p = world.player;
    const P = this.parts, g = this.group;
    const x = lerp(p.px, p.x, alpha), y = lerp(p.py, p.y, alpha), z = lerp(p.pz, p.z, alpha);

    // In first-person the camera sits in Benjen's head — hide his body so it
    // doesn't fill the screen (the sword trail still shows on swings).
    g.visible = !world._firstPerson;

    this.renderHeading = angleDamp(this.renderHeading, p.heading, 18, dt);
    g.rotation.set(0, this.renderHeading, 0);
    g.position.set(x, y, z);

    // reset pose (relaxed guard: Masamune angled forward at his side, elbow bent)
    this._breath = (this._breath || 0) + dt;
    P.legL.rotation.x = 0; P.legR.rotation.x = 0;
    P.kneeL.rotation.x = 0; P.kneeR.rotation.x = 0;
    P.shL.rotation.set(0, 0, 0.08); P.shR.rotation.set(-0.3, 0, -0.1);
    P.elbL.rotation.set(-0.2, 0, 0); P.elbR.rotation.set(-0.5, 0, 0);
    P.torso.rotation.set(0, 0, 0);
    P.torso.position.y = 1.28 + Math.sin(this._breath * 1.8) * 0.008;
    P.cape.rotation.x = 0.05;
    P.swordPivot.rotation.set(0.5, 0, 0);   // ready-carry: blade leans forward, not flagpole-vertical
    P.blade.material.emissiveIntensity = this._baseBladeEmiss;

    const moving = Math.hypot(p.vx, p.vz);
    let trailActive = false;

    switch (p.state) {
      case 'idle':
      case 'run': {
        this.phase += (0.6 + moving) * dt * 2.4;
        const amt = Math.min(1, moving / PLAYER_SPEED);
        const sw = Math.sin(this.phase) * amt;
        P.legL.rotation.x = sw * 0.7; P.legR.rotation.x = -sw * 0.7;
        P.kneeL.rotation.x = Math.max(0, -sw) * 1.0;              // knee lift on the recovering leg
        P.kneeR.rotation.x = Math.max(0, sw) * 1.0;
        P.shL.rotation.x = -sw * 0.5; P.shR.rotation.x = -0.3 + sw * 0.25;
        P.elbL.rotation.x = -0.2 - Math.abs(sw) * 0.4;
        P.torso.rotation.x = amt * (p.sprinting ? 0.24 : 0.1);    // lean into the run
        P.swordPivot.rotation.x = 0.5 + amt * 0.35;               // blade trails as he runs
        P.cape.rotation.x = 0.05 + amt * (p.sprinting ? 0.6 : 0.35) + Math.sin(this.phase * 0.5) * 0.03;
        g.position.y += Math.abs(Math.sin(this.phase)) * 0.05 * amt;
        break;
      }
      case 'attack': {
        this._swing(p);
        const t = p.attackT;
        trailActive = t > 0.05 && t < (p.atkNode ? p.atkNode.dur * 0.75 : 0.4);
        break;
      }
      case 'dodge': {
        const t = clamp01(p.dodgeT / DODGE_TIME);
        g.rotation.x = -TAU * t;                 // forward somersault (YXZ order → rolls along facing)
        P.legL.rotation.x = 1.3; P.legR.rotation.x = 1.3;         // full tuck
        P.kneeL.rotation.x = 1.6; P.kneeR.rotation.x = 1.6;
        P.shL.rotation.x = 0.9; P.shR.rotation.x = 0.9;
        P.elbL.rotation.x = -1.2; P.elbR.rotation.x = -1.2;
        P.cape.rotation.x = 0.6;
        break;
      }
      case 'block': {
        // Masamune raised across the body, off-hand braced behind the flat
        P.shR.rotation.set(-1.0, 0, -0.35);
        P.elbR.rotation.x = -1.15;
        P.swordPivot.rotation.set(0.15, 0, 1.2);                  // blade horizontal across
        P.shL.rotation.set(-0.75, 0, 0.4); P.elbL.rotation.x = -1.0;
        P.legL.rotation.x = 0.25; P.legR.rotation.x = -0.25;
        P.kneeL.rotation.x = 0.25; P.kneeR.rotation.x = 0.25;     // braced crouch
        P.torso.rotation.x = 0.08;
        break;
      }
      case 'hurt': {
        g.rotation.x = -0.3;
        break;
      }
      case 'titanfall': {
        this.phase += dt * 6;
        P.shR.rotation.x = -2.2 + Math.sin(this.phase) * 0.2;
        P.elbR.rotation.x = -0.3;
        P.shL.rotation.x = -1.5; P.elbL.rotation.x = -0.4;
        P.cape.rotation.x = 0.9 + Math.sin(this.phase * 2) * 0.08; // cape whipped up by the storm
        P.blade.material.emissiveIntensity = 2.4 + Math.sin(world.simTime * 20) * 0.8;
        g.position.y += 0.15;
        trailActive = true;
        // ember + black aura — Math.random() (render-only), NEVER world.rng():
        // this runs in the render loop, so pulling from the seeded sim stream a
        // vsync-variable number of times per tick would desync the 60 Hz sim.
        for (let i = 0; i < 2; i++) {
          const ang = Math.random() * 6.28, r = 1 + Math.random() * 2;
          world.spawnSpark(x + Math.cos(ang) * r, 0.4 + Math.random() * 2.5, z + Math.sin(ang) * r,
            Math.cos(ang) * 2, 3 + Math.random() * 4, Math.sin(ang) * 2, 0.4, 1.0, Math.random() < 0.5 ? 0 : 3);
        }
        break;
      }
      case 'dead': {
        // accumulate in a dedicated field — g.rotation.x is zeroed every frame
        // at the top of update(), so it can't hold the topple angle itself.
        this._deadLean = Math.min((this._deadLean || 0) + dt * 2, 1.4);
        g.rotation.x = this._deadLean;
        break;
      }
    }
    if (p.state !== 'dodge' && p.state !== 'hurt' && p.state !== 'dead') g.rotation.x = 0;

    g.updateMatrixWorld(true);

    // feed the sword trail with the blade's world-space endpoints
    this._top.set(0, 0.85, 0); this.parts.blade.localToWorld(this._top);
    this._bot.set(0, -0.85, 0); this.parts.blade.localToWorld(this._bot);
    this.trail.update(trailActive, this._top, this._bot);
  }

  // ---- per-move swing choreography ------------------------------------------
  // Each combo node maps to an archetype that matches its hit shape: Draw Cut is
  // a horizontal right→left slash, Return Slash the backhand, Ember Rise a
  // launcher uppercut, Hilt Crash a pommel jab, the sweeps full-body reaps.
  // The blade crosses centre exactly at the node's hit-window midpoint.
  _swing(p) {
    const P = this.parts;
    const node = p.atkNode;
    const type = SWING[node ? node.id : ''] || 'chop';
    const t = p.attackT;
    const h = node && node.hits && node.hits[0];
    const mid = h ? (h.t0 + h.t1) * 0.5 : 0.2;
    // eased progress that hits 0.5 at the strike moment, 1 at follow-through
    const q = clamp01(t / (mid * 2));
    const e = q * q * (3 - 2 * q);                       // smoothstep 0→1 (one-way cuts)
    const pulse = Math.sin(q * Math.PI);                 // 0→1→0 (out-and-back cuts)

    // shared footwork
    P.legL.rotation.x = 0.22; P.legR.rotation.x = -0.22; P.kneeR.rotation.x = 0.2;
    P.cape.rotation.x = 0.1 + pulse * 0.3;

    switch (type) {
      case 'slashR': {                                   // Draw Cut — his left → right, blade in line with the arm
        P.shR.rotation.set(-1.35, 1.25 - e * 2.5, 0);    // YXZ: lift, then yaw fans across
        P.elbR.rotation.x = -0.15;
        P.swordPivot.rotation.set(2.75, 0, 0.25);        // blade extends the arm, raked for the cut
        P.torso.rotation.y = -0.45 + e * 0.9;            // hips drive with the sweep
        P.shL.rotation.set(-0.4, 0, 0.3);
        break;
      }
      case 'slashL': {                                   // Return Slash — the backhand, right → left
        P.shR.rotation.set(-1.35, -1.25 + e * 2.5, 0);
        P.elbR.rotation.x = -0.15;
        P.swordPivot.rotation.set(2.75, 0, -0.25);
        P.torso.rotation.y = 0.45 - e * 0.9;
        P.shL.rotation.set(-0.4, 0, 0.3);
        break;
      }
      case 'rise': {                                     // rising diagonal — low left hip to high right
        P.shR.rotation.set(-0.4 - e * 1.5, 0.9 - e * 1.7, 0);
        P.elbR.rotation.x = -0.3;
        P.swordPivot.rotation.set(2.6, 0, 0.35);         // blade trails the arm through the rise
        P.torso.rotation.x = 0.1 - e * 0.18;             // opens up through the rise
        P.torso.rotation.y = -0.3 + e * 0.6;
        P.shL.rotation.x = -0.3 - e * 0.3;
        break;
      }
      case 'upper': {                                    // launcher — whip skyward
        P.shR.rotation.x = -0.15 - pulse * 2.5;
        P.elbR.rotation.x = -0.7 + pulse * 0.55;
        P.swordPivot.rotation.set(0.35, 0, -0.2);
        P.torso.rotation.x = 0.12 - pulse * 0.24;        // lean back with the launch
        P.shL.rotation.x = pulse * 0.5;
        P.kneeL.rotation.x = pulse * 0.3;
        break;
      }
      case 'bash': {                                     // Hilt Crash — pommel-first jab
        P.shR.rotation.set(-1.15 - e * 0.25, 0, 0);
        P.elbR.rotation.x = -1.5 + e * 1.4;              // arm punches out
        P.swordPivot.rotation.set(1.5, 0, 0);            // blade tipped back, hilt leads
        P.torso.rotation.y = 0.35 - e * 0.55;
        P.shL.rotation.x = -0.5;
        break;
      }
      case 'sweep': {                                    // full-body reap (Spiral/Cinder/BLACK MASAMUNE)
        P.shR.rotation.set(-1.35, 1.7 - e * 3.4, 0);     // huge fan, left → right
        P.elbR.rotation.x = -0.1;
        P.swordPivot.rotation.set(2.75, 0, 0.3);         // blade fully extended off the arm
        P.torso.rotation.y = -0.55 + e * 1.1;            // torso hurls through
        P.shL.rotation.set(-0.9, 0, 0.4);
        P.elbL.rotation.x = -0.8;
        P.kneeL.rotation.x = 0.3;
        break;
      }
      default: {                                         // overhead chop (Ashfall, Ruin Hammer, Falling Cut)
        const chop = pulse;
        P.shR.rotation.x = -1.9 + chop * 3.1;
        P.shR.rotation.z = -chop * 0.25;
        P.elbR.rotation.x = -0.9 + chop * 1.1;
        P.swordPivot.rotation.set(0.15 + chop * 0.45, 0, -0.15);
        P.shL.rotation.x = -1.4 + chop * 2.6;            // both hands on the big cuts
        P.elbL.rotation.x = -0.6 + chop * 0.7;
        P.torso.rotation.x = chop * 0.16;
        break;
      }
    }
  }
}

// combo node id → swing archetype (matches each node's name + hit arc)
const SWING = {
  N1: 'slashR',   // Draw Cut
  N2: 'slashL',   // Return Slash
  N3: 'rise',     // Rising Arc
  N4: 'slashR',   // Cross Reap
  N5: 'sweep',    // Spiral Cut
  N6: 'chop',     // Ashfall
  C1: 'upper',    // Ember Rise (launcher)
  C2: 'bash',     // Hilt Crash (stun)
  C3: 'sweep',    // Cinder Sweep
  C4: 'chop',     // Ruin Hammer
  C5: 'sweep',    // BLACK MASAMUNE
  JUMP: 'chop',   // Falling Cut
};
